import {
  FYDEOS_MANAGED_COPILOT_PROFILE_ID,
  getCopilotRouteOptionsQuery,
} from '@affine/graphql';
import { signal } from '@preact/signals-core';
import { Service } from '@toeverything/infra';

import type { GraphQLService, SubscriptionService } from '../../cloud';
import type { GlobalStateService } from '../../storage';
import { setWorkspaceAIRouteTarget } from './route-target';

const ROUTE_TARGET_KEY = 'AIManagedRouteTarget';

export interface AIModel {
  id: string;
  name: string;
  category: string;
  version: string;
  available: boolean;
}

export class AIModelService extends Service {
  readonly modelId = signal<string | undefined>(undefined);
  readonly models = signal<AIModel[]>([]);
  readonly defaultModelId = signal<string | undefined>(undefined);

  private workspaceId: string | undefined;
  private routeId: string | undefined;
  private requestId = 0;

  constructor(
    private readonly globalStateService: GlobalStateService,
    private readonly gqlService: GraphQLService,
    subscriptionService: SubscriptionService
  ) {
    super();
    const subscription = subscriptionService.subscription.ai$.subscribe(() => {
      if (this.workspaceId && this.routeId) {
        this.load(this.workspaceId, this.routeId).catch(console.error);
      }
    });
    this.disposables.push(() => subscription.unsubscribe());
  }

  setScope(workspaceId: string, routeId: string) {
    if (workspaceId === this.workspaceId && routeId === this.routeId) return;
    this.workspaceId = workspaceId;
    this.routeId = routeId;
    setWorkspaceAIRouteTarget(workspaceId, undefined);
    this.models.value = [];
    this.modelId.value = undefined;
    this.defaultModelId.value = undefined;
    this.load(workspaceId, routeId).catch(console.error);
  }

  resetModel() {
    this.setModel(this.defaultModelId.value);
  }

  setModel(modelId: string | undefined) {
    if (!this.workspaceId || !this.routeId) return;
    const workspaceId = this.workspaceId;
    if (
      modelId &&
      !this.models.value.find(model => model.id === modelId)?.available
    ) {
      return;
    }
    this.modelId.value = modelId;
    setWorkspaceAIRouteTarget(workspaceId, modelId);
    this.globalStateService.globalState.set(
      this.storageKey(this.workspaceId, this.routeId),
      modelId
    );
  }

  private async load(workspaceId: string, routeId: string) {
    const requestId = ++this.requestId;
    const result = await this.gqlService.gql({
      query: getCopilotRouteOptionsQuery,
      variables: { workspaceId, promptName: routeId },
    });
    if (requestId !== this.requestId) return;
    const options = result.currentUser?.copilot?.routeOptions;
    if (!options) {
      this.models.value = [];
      this.modelId.value = undefined;
      this.defaultModelId.value = undefined;
      setWorkspaceAIRouteTarget(workspaceId, undefined);
      return;
    }
    this.models.value = options.choices.map(choice => {
      const parts = choice.displayName.split(' ');
      const isFydeosChoice =
        choice.id === FYDEOS_MANAGED_COPILOT_PROFILE_ID ||
        choice.id.startsWith('fydeos-byok:');
      const category = isFydeosChoice ? parts.slice(0, 2).join(' ') : parts[0];
      return {
        id: choice.id,
        name: choice.displayName,
        category,
        version: parts.slice(isFydeosChoice ? 2 : 1).join(' '),
        available: choice.available,
      };
    });
    this.defaultModelId.value = options.defaultTargetId ?? undefined;
    const selected = this.globalStateService.globalState.get<string>(
      this.storageKey(workspaceId, routeId)
    );
    const selectedOrDefault = selected ?? this.defaultModelId.value;
    const selectedAvailable = this.models.value.some(
      model => model.id === selectedOrDefault && model.available
    );
    this.modelId.value = selectedAvailable ? selectedOrDefault : undefined;
    setWorkspaceAIRouteTarget(workspaceId, this.modelId.value);
    if (selected && !selectedAvailable) {
      this.globalStateService.globalState.set(
        this.storageKey(workspaceId, routeId),
        undefined
      );
    }
  }

  private storageKey(workspaceId: string, routeId: string) {
    return `${ROUTE_TARGET_KEY}:${workspaceId}:${routeId}`;
  }
}
